import { User, Session } from '@supabase/supabase-js';
import { supabaseManager } from '@/services/supabaseManager';
import type { AuthStoreGet, AuthStoreSet } from './auth.types';
import { queryClient } from '@/lib/queryClient';
import { queryKeys } from '@/lib/queryKeys';

/**
 * Core auth state, session refresh, and small cross-cutting utilities.
 */
export const createSessionActions = (set: AuthStoreSet, get: AuthStoreGet) => ({
  setUser: (user: User | null) =>
    set({ user, isAuthenticated: !!(user && get().session) }),
  setSession: (session: Session | null) =>
    set({
      session,
      isAuthenticated: !!(get().user && session),
    }),

  clearAuthState: () =>
    set({
      user: null,
      session: null,
      simpleUser: null,
      activeOperator: null,
      isAuthenticated: false,
      loading: false,
      isInWaitingScreen: false,
    }),

  fetchUserProfile: async (userId: string) => {
    try {
      const client = supabaseManager.getCurrentClient();
      const { data } = await client.from('profiles').select('*').eq('id', userId).maybeSingle();
      if (data) {
        // TanStack Query is the source of truth for profile data.
        queryClient.setQueryData(queryKeys.profile(userId), data);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  },

  refreshSession: async (): Promise<boolean> => {
    const applySession = (session: Session | null) => {
      if (!session) return false;
      set({ session, user: session.user, isAuthenticated: true });
      return true;
    };

    const tryRefreshForClient = async (client: ReturnType<typeof supabaseManager.getMainClient>) => {
      const { data, error } = await client.auth.refreshSession();
      if (error) throw error;
      return applySession(data.session);
    };

    try {
      try {
        const organizationClient = supabaseManager.getCurrentClient();
        if (await tryRefreshForClient(organizationClient)) {
          return true;
        }
      } catch {
        // Fall back to main client refresh.
      }

      const mainClient = supabaseManager.getMainClient();
      if (await tryRefreshForClient(mainClient)) {
        return true;
      }
      return false;
    } catch {
      return false;
    }
  },

  handleApiError: async (error: Error | null): Promise<boolean> => {
    const msg = (error?.message || '').toLowerCase();
    const rawMsg = error?.message || '';
    const isAuthError =
      msg.includes('jwt') ||
      msg.includes('expired') ||
      rawMsg.includes('PGRST301') ||
      rawMsg.includes('PGRST302') ||
      rawMsg.includes('PGRST116') ||
      msg.includes('406');

    if (isAuthError) {
      return await get().refreshSession();
    }
    return false;
  },
});
